import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Checkbox,
  ListItemText,
  Card,
  CardContent,
  Grid,
  LinearProgress
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  ViewColumn as ViewColumnIcon,
  Search as SearchIcon,
  GetApp as GetAppIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { endpoints, externalApiCall } from '../api/config';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import {
  MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX,
} from '../constants/muiNativeTableDenseLayout';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';

const PriceComparison = () => {
  const { token } = useAuth();
  const [showSummary, setShowSummary] = useState(true);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loadingRow, setLoadingRow] = useState(null);
  const [downloadingRow, setDownloadingRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    item_id: true,
    mp_cap_pricing: true,
    mp1: true,
    amazon_price: true,
    home_depot_price: true,
    lowes_price: true,
    recommended_price: true,
    recommended_price_including_lowes: true,
    reason: true,
    reason_including_lowes: true,
    run_date: true,
    run_version: true,
    created_at: true,
    lowes_job_version: true,
    lowes_scraped_at_date: true
  });

  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();
  const actionTextSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: 'primary.main',
    cursor: 'pointer',
    userSelect: 'none',
    textDecoration: 'none',
    textUnderlineOffset: '2px',
    '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
  };

  // Fetch version summary from Price Comparison API
  const versionsEndpoint = 'https://scrapers-price-comparison-production.up.railway.app/api/price-comparison-versions';
  const pcUsername = 'progress-admin';
  const pcPassword = 'VR6qjCLCxXa4kBbu8aUT';

  const fetchVersionSummary = async () => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const resp = await fetch(versionsEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ username: pcUsername, password: pcPassword })
      });
      if (!resp.ok) {
        const msg = await resp.text().catch(() => resp.statusText);
        throw new Error(msg || `HTTP ${resp.status}`);
      }
      const payload = await resp.json().catch(() => []);

      const rows = [];
      const today = new Date().toISOString().split('T')[0];

      const pushEntry = (entry, fallbackDate) => {
        if (entry === null || entry === undefined) return;
        if (typeof entry === 'number' || (typeof entry === 'string' && entry.trim() !== '')) {
          rows.push({ date: String(fallbackDate || today), version: String(entry) });
          return;
        }
        if (typeof entry === 'object') {
          const versionNum = (entry.version_number ?? entry.run_version ?? entry.version ?? entry.v ?? null);
          let runDate = entry.run_date ?? entry.date ?? fallbackDate ?? today;
          if (typeof runDate === 'string' && runDate.includes('T')) runDate = runDate.split('T')[0];
          if (versionNum !== null && versionNum !== undefined) rows.push({ date: String(runDate), version: String(versionNum) });
        }
      };

      const unwrap = (obj) => {
        if (!obj) return obj;
        if (Array.isArray(obj)) return obj;
        if (Array.isArray(obj.data)) return obj.data;
        if (Array.isArray(obj.results)) return obj.results;
        if (Array.isArray(obj.records)) return obj.records;
        return obj; // may be shape with versions/date keyed
      };

      const top = unwrap(payload);
      if (Array.isArray(top)) {
        top.forEach((e) => pushEntry(e, today));
      } else if (top && typeof top === 'object') {
        if (Array.isArray(top.versions)) {
          const baseDate = top.date || today;
          top.versions.forEach((v) => pushEntry(v, baseDate));
        } else {
          Object.entries(top).forEach(([key, value]) => {
            if (Array.isArray(value)) value.forEach((v) => pushEntry(v, key));
          });
        }
      }

      // Deduplicate
      const dedup = new Map();
      rows.forEach((r) => dedup.set(`${r.date}_${r.version}`, r));
      const uniqueRows = Array.from(dedup.values());
      // Sort by date desc first, then version desc
      uniqueRows.sort((a, b) => {
        const byDate = String(b.date).localeCompare(String(a.date));
        if (byDate !== 0) return byDate;
        const av = parseInt(a.version, 10);
        const bv = parseInt(b.version, 10);
        if (!isNaN(av) && !isNaN(bv) && av !== bv) return bv - av;
        return 0;
      });
      setSummaryRows(uniqueRows);
    } catch (e) {
      setSummaryError(e.message || 'Failed to load versions');
      setSummaryRows([]);
    } finally {
      setSummaryLoading(false);
    }
  };

  React.useEffect(() => { fetchVersionSummary(); }, []);

  const columns = [
    { id: 'id', label: 'ID' },
    { id: 'item_id', label: 'Item ID' },
    { id: 'mp_cap_pricing', label: 'MP Cap Pricing' },
    { id: 'mp1', label: 'MP1' },
    { id: 'amazon_price', label: 'Amazon Price' },
    { id: 'home_depot_price', label: 'Home Depot Price' },
    { id: 'lowes_price', label: 'Lowes Price' },
    { id: 'recommended_price', label: 'Recommended Price' },
    { id: 'recommended_price_including_lowes', label: 'Recommended Price (Incl. Lowes)' },
    { id: 'reason', label: 'Reason' },
    { id: 'reason_including_lowes', label: 'Reason (Incl. Lowes)' },
    { id: 'run_date', label: 'Run Date' },
    { id: 'run_version', label: 'Run Version' },
    { id: 'created_at', label: 'Created At' },
    { id: 'lowes_job_version', label: 'Lowes Job Version' },
    { id: 'lowes_scraped_at_date', label: 'Lowes Scraped Date' }
  ];

  const fetchDetailedData = async (date, version, rowIndex) => {
    setLoadingRow(rowIndex);
    setError(null);
    
    try {
      const url = `${endpoints.priceComparison}?date=${date}&version=${version}&format=json`;
      
      // Custom API call with JSON body
      const credentials = btoa('progress-admin:VR6qjCLCxXa4kBbu8aUT');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          username: 'progress-admin',
          password: 'VR6qjCLCxXa4kBbu8aUT'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data && Array.isArray(data)) {
        setData(data);
        setShowSummary(false);
        setSnackbar({
          open: true,
          message: `Successfully loaded ${data.length} price comparison records`,
          severity: 'success'
        });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching price comparison data:', err);
      setError(err.message || 'Failed to fetch price comparison data');
      setSnackbar({
        open: true,
        message: 'Failed to fetch price comparison data',
        severity: 'error'
      });
    } finally {
      setLoadingRow(null);
    }
  };

  const handleRefreshClick = async () => {
    if (data && data.length > 0) {
      // Get the first record to extract date and version
      const firstRecord = data[0];
      const runDate = new Date(firstRecord.run_date).toISOString().split('T')[0];
      const runVersion = firstRecord.run_version;
      await fetchDetailedData(runDate, runVersion);
    }
  };

  const handleBackToSummary = () => {
    setShowSummary(true);
    setData(null);
    setError(null);
    setSearchTerm('');
  };

  const downloadCSVForRow = async (date, version, rowIndex) => {
    setDownloadingRow(rowIndex);
    setError(null);
    
    try {
      const url = `${endpoints.priceComparison}?date=${date}&version=${version}&format=csv`;
      
      // Custom API call with JSON body
      const credentials = btoa('progress-admin:VR6qjCLCxXa4kBbu8aUT');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json',
          'Accept': 'text/csv'
        },
        body: JSON.stringify({
          username: 'progress-admin',
          password: 'VR6qjCLCxXa4kBbu8aUT'
        })
      });

      if (!response.ok) {
        const errorData = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const csvData = await response.text();
      
      // Create and download the CSV file
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      link.download = `price-comparison-${date}-v${version}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      setSnackbar({
        open: true,
        message: `Successfully downloaded CSV for ${date} version ${version}`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error downloading CSV:', err);
      setError(err.message || 'Failed to download CSV');
      setSnackbar({
        open: true,
        message: 'Failed to download CSV',
        severity: 'error'
      });
    } finally {
      setDownloadingRow(null);
    }
  };

  const handleColumnToggle = (columnId) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const filteredData = useMemo(() => {
    if (!data) return [];
    
    return data.filter(row => {
      const searchLower = searchTerm.toLowerCase();
      return Object.values(row).some(value => 
        value && value.toString().toLowerCase().includes(searchLower)
      );
    });
  }, [data, searchTerm]);

  const visibleColumnsList = columns.filter(col => visibleColumns[col.id]);
  const summaryGridRows = useMemo(
    () => summaryRows.map((row, idx) => ({ id: `${row.date}_${row.version}_${idx}`, __idx: idx, ...row })),
    [summaryRows]
  );
  const summaryGridColumns = useMemo(
    () => [
      { field: 'date', headerName: 'Date', resizable: true, flex: 1, minWidth: 150 },
      { field: 'version', headerName: 'Version', resizable: true, flex: 0.8, minWidth: 120 },
      {
        field: 'actions',
        headerName: 'Actions',
        resizable: false,
        sortable: false,
        filterable: false,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box
              component="span"
              role="button"
              tabIndex={downloadingRow === params.row.__idx ? -1 : 0}
              onClick={() => downloadCSVForRow(params.row.date, params.row.version, params.row.__idx)}
              onKeyDown={(e) => {
                if (downloadingRow === params.row.__idx) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  downloadCSVForRow(params.row.date, params.row.version, params.row.__idx);
                }
              }}
              sx={{
                ...actionTextSx,
                opacity: downloadingRow === params.row.__idx ? 0.6 : 1,
                pointerEvents: downloadingRow === params.row.__idx ? 'none' : 'auto',
              }}
            >
              {downloadingRow === params.row.__idx ? <CircularProgress size={14} /> : <GetAppIcon fontSize="small" />}
              Download
            </Box>
          </Box>
        ),
      },
    ],
    [downloadingRow]
  );
  const mainGridColumns = useMemo(
    () =>
      visibleColumnsList.map((column) => ({
        field: column.id,
        headerName: column.label,
        resizable: true,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const val = params.row?.[column.id];
          if (val === null || val === undefined || val === '') return '-';
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val);
        },
      })),
    [visibleColumnsList]
  );
  const mainGridRows = useMemo(
    () =>
      filteredData.map((row, idx) => ({
        ...row,
        id: row.id ?? row.item_id ?? `${row.run_date ?? 'row'}-${row.run_version ?? 'v'}-${idx}`,
      })),
    [filteredData]
  );

  const downloadCSV = () => {
    if (!data || data.length === 0) return;

    const csvHeaders = visibleColumnsList.map(col => col.label).join(',');
    const csvRows = data.map(row => 
      visibleColumnsList.map(col => {
        const value = row[col.id];
        return value !== null && value !== undefined ? `"${value}"` : '""';
      }).join(',')
    );

    const csvContent = [csvHeaders, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `price-comparison-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const renderSummaryTable = () => (
    <Paper elevation={2} sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
        <GlobalDenseModeToolbar
          denseMode={denseMode}
          denseModeTogglePending={denseModeTogglePending}
          persistDenseMode={persistDenseMode}
        />
      </Box>
      {summaryError ? <Box sx={{ p: 1, color: 'error.main', fontSize: '0.8125rem' }}>{summaryError}</Box> : null}
      <HoverExpandDataGridPro
        rows={summaryGridRows}
        columns={summaryGridColumns}
        loading={summaryLoading}
        disableVirtualization
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        pagination
        paginationMode="client"
        pageSizeOptions={[5, 10, 25, 50, 100, 1000, { value: -1, label: 'All' }]}
        initialState={{ pagination: { paginationModel: { pageSize: 1000, page: 0 } } }}
        rowHeight={denseMode ? 36 : 52}
        columnHeaderHeight={denseMode ? 40 : 56}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Paper>
  );

  const renderMainTable = () => (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        mb: 1.5, 
        display: 'flex', 
        alignItems: 'center',
        gap: 1,
        width: '100%'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto' }}>
          <TextField
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              sx: { fontSize: '0.875rem' }
            }}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onClick={handleBackToSummary}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBackToSummary();
              }
            }}
            sx={actionTextSx}
          >
            <VisibilityIcon fontSize="small" />
            Back to Summary
          </Box>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, p: 0.5, px: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontSize: '1.125rem' }}>
            Marketplace Pricing ({filteredData.length} total)
          </Typography>
          </Box>
          <Box sx={{ ...MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX, gap: 0.5, flexWrap: 'wrap' }}>
            <Tooltip title="Refresh data">
              <IconButton onClick={handleRefreshClick} disabled={isLoading} size="small" aria-label="Refresh data" sx={{ padding: '6px' }}>
                {isLoading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <GlobalDenseModeToolbar
              denseMode={denseMode}
              denseModeTogglePending={denseModeTogglePending}
              persistDenseMode={persistDenseMode}
            />
            <Tooltip title="Show/Hide Columns">
              <IconButton onClick={handleMenuOpen} size="small" aria-label="Show/Hide Columns" sx={{ padding: '6px' }}>
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{ sx: { maxHeight: '70vh', minWidth: 220, p: 1 } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show/Hide Columns</Typography>
                <IconButton size="small" onClick={handleMenuClose} sx={{ p: 0.5 }} aria-label="Close column visibility menu">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              {columns.map((column) => (
                <MenuItem key={column.id} onClick={() => handleColumnToggle(column.id)} sx={{ fontSize: '0.875rem', minHeight: 'auto', py: 0.75, px: 1 }}>
                  <Checkbox checked={visibleColumns[column.id]} size="small" sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={column.label} primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.2 } }} />
                </MenuItem>
              ))}
            </Menu>
            <Tooltip title="Download CSV">
              <IconButton onClick={downloadCSV} size="small" aria-label="Download CSV" disabled={!data || data.length === 0} sx={{ padding: '6px' }}>
                <GetAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {error ? <Box sx={{ p: 1, color: 'error.main', fontSize: '0.8125rem' }}>{String(error?.message || error)}</Box> : null}
        <HoverExpandDataGridPro
          rows={mainGridRows}
          columns={mainGridColumns}
          loading={isLoading}
          disableVirtualization
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          pagination
          paginationMode="client"
          pageSizeOptions={[5, 10, 25, 50, 100, 1000, { value: -1, label: 'All' }]}
          initialState={{ pagination: { paginationModel: { pageSize: 1000, page: 0 } } }}
          rowHeight={denseMode ? 36 : 52}
          columnHeaderHeight={denseMode ? 40 : 56}
          sx={{ flex: 1, minHeight: 0 }}
        />
      </Paper>
    </Box>
  );

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 1.5, fontWeight: 'bold', color: 'primary.main', fontSize: '1.25rem' }}>
        Marketplace Pricing
      </Typography>

        {isLoading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {showSummary ? renderSummaryTable() : renderMainTable()}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default PriceComparison;
